<script setup lang="ts">
import { EStoreModules } from '@/store/storeType'
import {
    BaseDialog,
    CheckboxField,
    DateTimePickerField,
    DeviceSelector,
    FullScreenLoader,
    NumberInputField,
    PrimaryButton,
    StackLayout
} from '@rfc/rfc-components-library'
import { detectionService } from '@rfc/rfc-components-library/services'
import { EAlertActions, type DetectionCreateModel } from '@rfc/rfc-components-library/types'
import moment from 'moment'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const store = useStore()
const { t } = useI18n()

type Props = {
    deviceId?: number
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const open = ref(false)
const loading = ref(false)
const submitted = ref(false)

const hasErrors = computed(() => ({
    bib: submitted.value && !data.value.bib,
    timestamp: submitted.value && !data.value.timestamp
}))

const data = ref<Partial<Omit<DetectionCreateModel, 'timestamp'> & { timestamp: Date }>>({
    device_id: props.deviceId
})

const confirmRental = () => {
    submitted.value = true
    if (!props.deviceId || !data.value?.bib) return
    loading.value = true
    detectionService
        .createDetection({
            ...data.value,
            device_id: props.deviceId ?? data.value?.device_id,
            timestamp: data.value.timestamp
                ? parseInt(moment(data.value.timestamp).utc(true).format('X'))
                : parseInt(moment().utc(true).format('X'))
        })
        .then(() => {
            open.value = false
            data.value = {}

            store?.dispatch(`${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`, {
                action: EAlertActions.SUCCESS,
                message: t('device.actions.addDetection.createDetectionSuccess')
            })

            setTimeout(() => emit('refresh'), 5000)
        })
        .finally(() => {
            loading.value = false
            submitted.value = false
        })
}
</script>

<template>
    <FullScreenLoader :loading="loading" />
    <PrimaryButton
        :label="$t('device.actions.addDetection.action')"
        @click="open = true"
        v-bind="$attrs"
        class="action-button" />
    <BaseDialog
        :title="$t('device.actions.addDetection.title')"
        :open="open"
        @close="open = false"
        @confirm="confirmRental"
        class="dialog-with-dropdown">
        <template #content>
            <StackLayout direction="column" :gap="16" class="w-full">
                <DeviceSelector
                    v-if="!deviceId"
                    :label="$t('detection.model.device')"
                    :deviceId="data?.device_id"
                    @select="
                        data = { ...(data ?? {}), device_id: $event?.device_id ?? $event?.id }
                    " />
                <NumberInputField
                    :label="$t('detection.model.bib')"
                    :modelValue="data?.bib"
                    :hasError="hasErrors.bib"
                    @change="data = { ...(data ?? {}), bib: $event }" />
                <NumberInputField
                    :label="$t('detection.model.eventFilter')"
                    :modelValue="data?.event_filter"
                    @change="data = { ...(data ?? {}), event_filter: $event }" />
                <NumberInputField
                    :label="$t('detection.model.timerFilter')"
                    :modelValue="data?.timer_filter"
                    @change="data = { ...(data ?? {}), timer_filter: $event }" />
                <CheckboxField
                    :label="$t('detection.model.withdrawal')"
                    :modelValue="!!data?.withdrawal"
                    @change="data = { ...(data ?? {}), withdrawal: $event }" />
                <DateTimePickerField
                    :label="$t('device.actions.addDetection.timestamp')"
                    :modelValue="data?.timestamp ? moment(data?.timestamp).toDate() : undefined"
                    :timepicker="{ enableSeconds: true }"
                    :hasError="hasErrors.timestamp"
                    @change="data = { ...(data ?? {}), timestamp: $event ?? undefined }"
                    class="w-full" />
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.action-button {
    & :deep(.v-btn__content) {
        white-space: normal;
    }
}
</style>
